import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export enum OttopilotTrigger {
    dateTimeTrigger = "8904234e-6d19-4c9c-80fc-e9e2a849acad",
    noComponentTrigger = "96deaeac-72bd-45a5-b2b0-13de8771a7bd",
}

export enum OttopilotTriggerDataDriver {
    /** Value from `defaultValue` only. */
    staticDefault = "a7b8c9d0-e1f2-4345-a678-9abcdef01234",
    /** Current workspace user id. */
    workspaceUser = "e38af7e6-174f-454e-b124-698ee9be1a0c",
    /** Workspace user template id (role). */
    workspaceUserTemplateId = "f49ea8f7-285f-565f-c235-709ff0ad2b1d",
    currentTimestamp = "d9c9f7ab-9a08-4568-87ae-1c7a38559853",
    /** Selected disposition id while editing Event Container details. */
    eventContainerDisposition = "87106b33-4c52-4407-8161-61997d91413c",
    /**
     * At event-create / Event Container-update time, copy from `referenceField` on an Event Container or Event when that value
     * is non-null; otherwise omit the target column from the payload entirely.
     */
    runTimeDefault = "f1a2b3c4-d5e6-4478-a012-3456789abcde",
}

/**
 * How the schedule column value is encoded for the create-event API.
 * `duluxIst`: picker value is turned into an IST wall string then `toIso8601FromIstSafe`.
 * `iso8601Instant`: `Date#toISOString()` or string passthrough.
 */
export enum OttopilotScheduleValueEncoding {
    duluxIst = "c8d9e0f1-a2b3-4455-c667-8bcdef012345",
    iso8601Instant = "d9e0f1a2-b3c4-4567-d778-9cdef0123456",
}

export type OttopilotTriggerDataColumn =
    | {
          columnId: Uuid;
          columnType: "OttopilotEventContainer" | "OttopilotEvent";
          driver: OttopilotTriggerDataDriver.staticDefault;
          defaultValue?: unknown;
      }
    | {
          columnId: Uuid;
          columnType: "OttopilotEventContainer" | "OttopilotEvent";
          driver: OttopilotTriggerDataDriver.workspaceUser;
      }
    | {
          columnId: Uuid;
          columnType: "OttopilotEventContainer" | "OttopilotEvent";
          driver: OttopilotTriggerDataDriver.workspaceUserTemplateId;
      }
    | {
          columnId: Uuid;
          columnType: "OttopilotEventContainer" | "OttopilotEvent";
          driver: OttopilotTriggerDataDriver.currentTimestamp;
      }
    | {
          columnId: Uuid;
          columnType: "OttopilotEventContainer" | "OttopilotEvent";
          driver: OttopilotTriggerDataDriver.eventContainerDisposition;
      }
    | {
          columnId: Uuid;
          columnType: "OttopilotEventContainer" | "OttopilotEvent";
          driver: OttopilotTriggerDataDriver.runTimeDefault;
          referenceField: Uuid;
          referenceFieldType: "OttopilotEventContainer" | "OttopilotEvent";
      };

export type OttopilotTriggerElement = {
    eventTemplateId: Uuid;
    triggerDispositions: Array<Uuid>;
    dataColumns: Array<OttopilotTriggerDataColumn>;
} & (
    | {
          type: OttopilotTrigger.dateTimeTrigger;
          dateTimeColumnId: Uuid;
          scheduleFieldLabel?: string;
          scheduleValueEncoding: OttopilotScheduleValueEncoding;
          /** On schedule value change, set these event columns to the selected disposition id. */
          syncSelectedDispositionToColumnIds?: Array<Uuid>;
      }
    | {
          type: OttopilotTrigger.noComponentTrigger;
      }
);

export type OttopilotTriggerConfig = Array<OttopilotTriggerElement>;
